import { useEffect, useRef, useState } from "react";
import { Mail, Send, Loader2, CheckCircle2, Linkedin } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import ShareWinModal from "../app/ShareWinModal";
import DateField from "../app/DateField";
import { STATUS_LABEL } from "./constants";
import { StepNav } from "./Stepper";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../ui/alert-dialog";

function Notes({ app, onUpdate }) {
  const [n, setN] = useState({ recruiter_name: app.recruiter_name || "", follow_up_date: app.follow_up_date || "", notes: app.notes || "" });
  const [state, setState] = useState("");
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    setState("saving");
    const t = setTimeout(async () => {
      try {
        await api.patch(`/applications/${app.id}/notes`, n);
        onUpdate(n);
        setState("saved");
      } catch {
        setState("error");
      }
    }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n]);
  return (
    <div className="gi-card p-5 sm:p-6" data-testid="application-notes">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-display text-lg font-bold text-ink">Private notes</h3>
        <span className="text-sm text-faint" data-testid="notes-save-state" aria-live="polite">
          {state === "saving" ? "Saving…" : state === "saved" ? "Saved" : state === "error" ? <span className="text-danger">Not saved. Check your connection.</span> : "Saves automatically"}
        </span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <div><label htmlFor="n-rec" className="gi-label">Recruiter or contact</label><input id="n-rec" data-testid="notes-recruiter-input" value={n.recruiter_name} onChange={(e) => setN((x) => ({ ...x, recruiter_name: e.target.value }))} placeholder="e.g. Jordan, HR" className="gi-input" /></div>
        <div><label htmlFor="n-date" className="gi-label">Follow up on</label><DateField id="n-date" testid="notes-followup-input" value={n.follow_up_date} onChange={(v) => setN((x) => ({ ...x, follow_up_date: v }))} /></div>
      </div>
      <div className="mt-3"><label htmlFor="n-notes" className="gi-label">Notes</label><textarea id="n-notes" data-testid="notes-text-input" rows={3} value={n.notes} onChange={(e) => setN((x) => ({ ...x, notes: e.target.value }))} placeholder="Interview dates, salary discussed, things to remember…" className="gi-input resize-y" /></div>
    </div>
  );
}

function EmailRecruiter({ app, onUpdate }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const unconfirmed = (app.skill_gaps?.items || []).filter((i) => i.status === "auto_added");
  const send = async (ok = false) => {
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return toast.error("Enter the recruiter's email address.");
    if (unconfirmed.length && ok !== true) return setConfirm(true);
    setSending(true);
    try {
      await api.post(`/applications/${app.id}/email`, { recruiter_email: email.trim() });
      onUpdate({ email_sent: true });
      toast.success(`Sent to ${email.trim()}. Replies go to your own inbox.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The email didn't send. Please try again.");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="gi-card p-5 sm:p-6">
      <h3 className="font-display text-lg font-bold text-ink flex items-center gap-2"><Mail className="w-5 h-5 text-brand" /> Email it to a recruiter <span className="text-sm font-normal text-faint">(optional)</span></h3>
      <p className="text-[15px] text-body mt-1 mb-4">We'll send your tailored CV and cover letter. Replies go straight to your inbox.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <label htmlFor="rec-email" className="sr-only">Recruiter email</label>
        <input id="rec-email" data-testid="recruiter-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="recruiter@company.com" className="gi-input flex-1" />
        <button data-testid="send-cv-email-button" onClick={() => send()} disabled={sending} className="gi-btn gi-btn-secondary">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {app.email_sent ? "Send again" : "Send"}</button>
      </div>
      {app.email_sent && <p className="mt-3 text-sm text-success flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Email sent.</p>}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="skill-gaps-send-confirm" className="bg-surface border-line text-ink">
          <AlertDialogHeader>
            <AlertDialogTitle>Before you send</AlertDialogTitle>
            <AlertDialogDescription className="text-body">Your CV still lists skills you haven't confirmed: <span className="text-ink font-medium">{unconfirmed.map((i) => i.skill).join(", ")}</span>. The recruiter may ask about each one.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="skill-gaps-send-review" className="border-line">Review them first</AlertDialogCancel>
            <AlertDialogAction data-testid="skill-gaps-send-anyway" className="bg-brand text-onbrand hover:bg-brand-hover" onClick={() => send(true)}>I can back them up, send</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function StepTrack({ app, onUpdate, onBack, onFinish }) {
  const [share, setShare] = useState(false);
  const setStatus = async (status) => {
    try {
      await api.patch(`/applications/${app.id}/status`, { status });
      onUpdate({ status });
      toast.success(`Updated: ${STATUS_LABEL[status]}`);
    } catch {
      toast.error("Couldn't update the status. Please try again.");
    }
  };
  return (
    <div className="af-fade-up space-y-5" data-testid="studio-step-track">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink">Track this application</h1>
        <p className="text-body mt-2">Keep the status up to date. It helps Kesh spot what's working for you.</p>
      </div>
      <div className="gi-card p-5 sm:p-6">
        <div className="gi-label" id="status-label">Where is this application now?</div>
        <div role="radiogroup" aria-labelledby="status-label" className="flex flex-wrap gap-2 mt-2" data-testid="status-options">
          {Object.entries(STATUS_LABEL).map(([k, l]) => (
            <button key={k} role="radio" aria-checked={app.status === k} data-testid={`status-${k}`} onClick={() => setStatus(k)}
              className={`px-4 py-2.5 min-h-[44px] rounded-xl border text-[15px] font-medium transition-colors ${app.status === k ? "border-brand bg-brand-soft text-brand-text ring-1 ring-brand" : "border-line bg-surface text-ink hover:border-brand/40"}`}>
              {app.status === k && <CheckCircle2 className="w-4 h-4 inline mr-1.5 -mt-0.5" />}{l}
            </button>
          ))}
        </div>
        {(app.status === "interview" || app.status === "offer") && (
          <button data-testid="share-win-button" onClick={() => setShare(true)} className="mt-4 gi-btn gi-btn-tertiary gi-btn-sm"><Linkedin className="w-4 h-4" /> Share this win on LinkedIn</button>
        )}
      </div>
      <Notes app={app} onUpdate={onUpdate} />
      <EmailRecruiter app={app} onUpdate={onUpdate} />
      <StepNav onBack={onBack} onNext={onFinish} nextLabel="Done, back to my applications" />
      <ShareWinModal open={share} onOpenChange={setShare} score={app.ats_score} kind={app.status === "offer" ? "offer" : "interview"} />
    </div>
  );
}
